import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, User, Phone, Shield, Briefcase, FileCheck,
  CheckCircle2, AlertCircle, Loader2, Upload, Camera, X, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { SectionCard, Field } from "@/components/FormUI";

const initialState = {
  company_afm: "", company_name: "",
  last_name: "", first_name: "",
  father_last_name: "", father_first_name: "",
  mother_last_name: "", mother_first_name: "",
  birth_date: "", nationality: "",
  // Greek ID
  id_card_number: "", id_issue_date: "", id_issue_dept: "",
  // Foreign ID
  passport_number: "", passport_issue_date: "", passport_country: "",
  foreign_residence_permit: "", foreign_residence_expiry: "",
  marital_status: "",
  children_count: "", children_under_18: "",
  phone_landline: "", phone_mobile: "", email: "", email_confirm: "",
  address: "",
  personal_afm: "", amka: "", am_ika: "", pa: "", first_insurance_year: "",
  general_experience_years: "", specialty_experience_years: "",
  hire_date: "", specialty: "",
  salary_type: "", salary_amount: "", salary_period: "",
  contract_type: "", contract_end_date: "",
  employment_type: "",
  schedule_type: "", schedule_hours: "", schedule_days: [],
  // Shift schedule: { "Δευτέρα": {start: "08:00", end: "16:00"}, ... }
  shift_schedule: {},
  // Free-form notes
  notes: "",
};

const DAYS_OF_WEEK = [
  { code: "Δευτέρα", short: "Δευ" },
  { code: "Τρίτη", short: "Τρί" },
  { code: "Τετάρτη", short: "Τετ" },
  { code: "Πέμπτη", short: "Πέμ" },
  { code: "Παρασκευή", short: "Παρ" },
  { code: "Σάββατο", short: "Σάβ" },
  { code: "Κυριακή", short: "Κυρ" },
];

const MIN_MONTHLY_2026 = 830; // basic salary 2026 (€) — warning threshold

// Greek-aware uppercase that strips tonos like real Greek uppercase
const upperEl = (v) => (v || "").toLocaleUpperCase("el-GR");

// Parse "07:00-19:00" → returns hours as float, or null if invalid
function parseHoursRange(str) {
  if (!str) return null;
  const m = str.match(/^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const [_, h1, m1, h2, m2] = m;
  const start = parseInt(h1, 10) + parseInt(m1, 10) / 60;
  const end = parseInt(h2, 10) + parseInt(m2, 10) / 60;
  let diff = end - start;
  if (diff <= 0) diff += 24; // overnight shift
  return diff;
}

// Auto-format messy hours input → HH:MM-HH:MM
// Accepts: "07-19", "7-19", "7:30-19", "07:00-19:30", etc.
function formatHoursRange(raw) {
  if (!raw) return "";
  const cleaned = raw.replace(/\s+/g, "").replace(/[—–−]/g, "-");
  const m = cleaned.match(/^(\d{1,2})(?::(\d{1,2}))?-(\d{1,2})(?::(\d{1,2}))?$/);
  if (!m) return raw;
  const pad = (s) => String(parseInt(s || "0", 10)).padStart(2, "0");
  const h1 = pad(m[1]), m1 = pad(m[2] || "0");
  const h2 = pad(m[3]), m2 = pad(m[4] || "0");
  return `${h1}:${m1}-${h2}:${m2}`;
}

// Calculate total hours for shift_schedule object
function calcShiftTotalHours(shiftObj) {
  if (!shiftObj || typeof shiftObj !== "object") return 0;
  let total = 0;
  for (const day of Object.values(shiftObj)) {
    if (!day || !day.start || !day.end) continue;
    const sh = parseHoursRange(`${day.start}-${day.end}`);
    if (sh !== null) total += sh;
  }
  return total;
}

export default function HiringForm() {
  const nav = useNavigate();
  // Lazy initializer: hydrate from localStorage synchronously to avoid empty-flash on edit-back
  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem("hireflow_draft");
      if (saved) return { ...initialState, ...JSON.parse(saved) };
    } catch (err) {
      console.warn("Failed to restore draft from localStorage:", err);
    }
    return initialState;
  });
  const [resumes, setResumes] = useState([]); // array of File objects (multiple files allowed)
  const [afmStatus, setAfmStatus] = useState("idle"); // idle | checking | valid | invalid
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const resumeInput = useRef(null);
  const photoInput = useRef(null);

  // Persist draft on every change
  useEffect(() => {
    localStorage.setItem("hireflow_draft", JSON.stringify(data));
  }, [data]);

  const set = (k) => (e) => setData({ ...data, [k]: e.target?.value ?? e });
  // Auto-uppercase setter (for Greek text fields)
  const setU = (k) => (e) => setData({ ...data, [k]: upperEl(e.target?.value ?? e) });

  // Real-time AFM validation (debounced)
  useEffect(() => {
    const afm = data.company_afm.trim();
    if (!afm) { setAfmStatus("idle"); return; }
    if (afm.length < 9) { setAfmStatus("idle"); return; }
    setAfmStatus("checking");
    const t = setTimeout(async () => {
      try {
        const res = await api.post("/afm/check", { afm });
        setAfmStatus(res.data.valid ? "valid" : "invalid");
      } catch (err) {
        console.warn("AFM check failed:", err);
        setAfmStatus("invalid");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [data.company_afm]);

  function validate() {
    const e = {};
    const required = [
      "company_afm", "company_name", "last_name", "first_name",
      "father_last_name", "father_first_name", "mother_last_name", "mother_first_name",
      "birth_date", "nationality", "phone_mobile", "address",
      "personal_afm", "amka",
      "specialty_experience_years",
      "hire_date", "specialty", "salary_type", "contract_type",
      "employment_type", "schedule_type",
    ];
    required.forEach((k) => {
      const v = data[k];
      if (v === undefined || v === null || String(v).trim() === "") {
        e[k] = "Υποχρεωτικό πεδίο";
      }
    });
    // general_experience_years: accept 0 explicitly
    {
      const v = String(data.general_experience_years ?? "").trim();
      if (v === "") e.general_experience_years = "Υποχρεωτικό πεδίο";
    }

    // Conditional: Greek nationality identity fields
    if (data.nationality === "greek") {
      ["id_card_number", "id_issue_date", "id_issue_dept"].forEach((k) => {
        if (!String(data[k] || "").trim()) e[k] = "Υποχρεωτικό πεδίο";
      });
    }
    // Conditional: Foreign nationality identity fields
    if (data.nationality === "foreign") {
      ["passport_number", "passport_issue_date", "passport_country",
       "foreign_residence_permit", "foreign_residence_expiry"].forEach((k) => {
        if (!String(data[k] || "").trim()) e[k] = "Υποχρεωτικό πεδίο";
      });
      // Expiry must be after today
      if (data.foreign_residence_expiry) {
        const exp = new Date(data.foreign_residence_expiry);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (!isNaN(exp.getTime()) && exp <= today) {
          e.foreign_residence_expiry = "Η ημερομηνία λήξης πρέπει να είναι μετά τη σήμερα";
        }
      }
    }
    // Conditional: salary amount when agreed
    if (data.salary_type === "agreed") {
      const v = String(data.salary_amount ?? "").trim();
      if (v === "" || isNaN(Number(v)) || Number(v) <= 0) {
        e.salary_amount = "Συμπληρώστε έγκυρο ποσό";
      }
      if (!data.salary_period) {
        e.salary_period = "Επιλέξτε είδος αμοιβής";
      }
    }
    // Conditional: contract end date when definite
    if (data.contract_type === "definite") {
      if (!String(data.contract_end_date || "").trim()) {
        e.contract_end_date = "Υποχρεωτικό πεδίο";
      }
    }
    // Conditional: schedule when fixed → hours + days
    if (data.schedule_type === "fixed") {
      const hrs = parseHoursRange(data.schedule_hours);
      if (hrs === null) {
        e.schedule_hours = "Μορφή ωραρίου: 07:00-19:00";
      }
      const days = Array.isArray(data.schedule_days) ? data.schedule_days : [];
      if (days.length === 0) {
        e.schedule_days = "Επιλέξτε τουλάχιστον μία ημέρα";
      }
      if (hrs !== null && days.length > 0 && hrs * days.length > 40) {
        e.schedule_overload = true;
      }
    }

    if (data.email && data.email_confirm && data.email !== data.email_confirm) {
      e.email_confirm = "Τα email δεν ταιριάζουν";
    }
    if (afmStatus !== "valid") {
      e.company_afm = "ΑΦΜ Επιχείρησης δεν βρέθηκε στο σύστημα";
    }
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    setGlobalError("");
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const first = document.querySelector('[data-error="true"]');
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (k === "email_confirm") return;
        if (k === "schedule_days") {
          fd.append(k, Array.isArray(v) ? v.join(",") : (v || ""));
          return;
        }
        if (k === "shift_schedule") {
          fd.append(k, v && typeof v === "object" ? JSON.stringify(v) : (v || ""));
          return;
        }
        fd.append(k, v ?? "");
      });
      resumes.forEach((f) => fd.append("resume_files", f));

      const res = await api.post("/submissions/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Pass to preview screen via sessionStorage (PDF base64 is large)
      sessionStorage.setItem("hireflow_preview", JSON.stringify(res.data));
      sessionStorage.setItem("hireflow_form_data", JSON.stringify(data));
      nav("/preview");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Σφάλμα κατά την υποβολή. Δοκιμάστε ξανά.";
      setGlobalError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls = (k) => {
    const isInvalid = errors[k];
    return `hf-input ${isInvalid ? "hf-error" : ""}`;
  };

  const afmInputCls = () => {
    if (afmStatus === "invalid") return "hf-input hf-error";
    if (afmStatus === "valid") return "hf-input hf-success";
    return "hf-input";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-blue-700 via-blue-700 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex items-center gap-2 text-blue-200 text-xs font-bold tracking-[0.2em] uppercase mb-4">
            <span className="inline-block w-8 h-px bg-blue-300"></span>
            HireFlow
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Φόρμα Πρόσληψης
          </h1>
          <p className="text-blue-100 mt-4 text-base md:text-lg max-w-2xl font-medium">
            Συμπληρώστε τα στοιχεία σας για την διαδικασία πρόσληψης. Όλα τα δεδομένα διαβιβάζονται με ασφάλεια στο λογιστήριο.
          </p>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="hiring-form">
        {/* Section 1: Company */}
        <SectionCard
          testId="section-company"
          icon={<Building2 size={22} strokeWidth={2.2} />}
          title="Στοιχεία Επιχείρησης"
          subtitle="Πληροφορίες της επιχείρησης πρόσληψης"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="ΑΦΜ Επιχείρησης" required error={errors.company_afm}>
              <div className="relative">
                <input
                  data-testid="company-afm-input"
                  data-error={Boolean(errors.company_afm)}
                  className={afmInputCls()}
                  value={data.company_afm}
                  onChange={(e) => {
                    // Strip non-digits, then take up to 12 chars
                    const cleaned = (e.target.value || "").replace(/\D/g, "").slice(0, 12);
                    setData({ ...data, company_afm: cleaned });
                  }}
                  inputMode="numeric"
                  maxLength={12}
                  placeholder="123456789"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {afmStatus === "checking" && <Loader2 className="text-slate-400 animate-spin" size={18} />}
                  {afmStatus === "valid" && <CheckCircle2 className="text-green-600" size={20} data-testid="afm-valid-icon" />}
                  {afmStatus === "invalid" && <AlertCircle className="text-red-500" size={20} data-testid="afm-invalid-icon" />}
                </div>
              </div>
              {afmStatus === "invalid" && (
                <p className="text-xs text-red-600 font-semibold mt-2" data-testid="afm-error-message">
                  ΑΦΜ Επιχείρησης δεν βρέθηκε στο σύστημα
                </p>
              )}
              {afmStatus === "valid" && (
                <p className="text-xs text-green-700 font-semibold mt-2" data-testid="afm-valid-message">
                  ✓ Επαληθεύθηκε
                </p>
              )}
            </Field>
            <Field label="Επωνυμία Επιχείρησης" required error={errors.company_name}>
              <input data-testid="company-name-input" className={inputCls("company_name")} value={data.company_name} onChange={setU("company_name")} />
            </Field>
          </div>
        </SectionCard>

        {/* Section 2: Personal */}
        <SectionCard
          testId="section-personal"
          icon={<User size={22} strokeWidth={2.2} />}
          title="Προσωπικά Στοιχεία"
          subtitle="Στοιχεία εργαζομένου"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Επίθετο" required error={errors.last_name}>
              <input data-testid="last-name-input" className={inputCls("last_name")} value={data.last_name} onChange={setU("last_name")} />
            </Field>
            <Field label="Όνομα" required error={errors.first_name}>
              <input data-testid="first-name-input" className={inputCls("first_name")} value={data.first_name} onChange={setU("first_name")} />
            </Field>
            <Field label="Επίθετο Πατέρα" required error={errors.father_last_name}>
              <input data-testid="father-last-name-input" className={inputCls("father_last_name")} value={data.father_last_name} onChange={setU("father_last_name")} />
            </Field>
            <Field label="Όνομα Πατέρα" required error={errors.father_first_name}>
              <input data-testid="father-first-name-input" className={inputCls("father_first_name")} value={data.father_first_name} onChange={setU("father_first_name")} />
            </Field>
            <Field label="Επίθετο Μητέρας" required error={errors.mother_last_name}>
              <input data-testid="mother-last-name-input" className={inputCls("mother_last_name")} value={data.mother_last_name} onChange={setU("mother_last_name")} />
            </Field>
            <Field label="Όνομα Μητέρας" required error={errors.mother_first_name}>
              <input data-testid="mother-first-name-input" className={inputCls("mother_first_name")} value={data.mother_first_name} onChange={setU("mother_first_name")} />
            </Field>
            <Field label="Ημερομηνία Γέννησης" required error={errors.birth_date}>
              <input data-testid="birth-date-input" type="date" className={inputCls("birth_date")} value={data.birth_date} onChange={set("birth_date")} />
            </Field>
            <Field label="Υπηκοότητα" required error={errors.nationality}>
              <select data-testid="nationality-select" className={`${inputCls("nationality")} hf-select`} value={data.nationality} onChange={set("nationality")}>
                <option value="">Επιλέξτε υπηκοότητα...</option>
                <option value="greek">Ελληνική</option>
                <option value="foreign">Αλλοδαπή</option>
              </select>
            </Field>

            {/* Conditional: Greek ID fields */}
            {data.nationality === "greek" && (
              <>
                <Field label="Αριθμός Ταυτότητας" required error={errors.id_card_number}>
                  <input data-testid="id-card-input" className={inputCls("id_card_number")} value={data.id_card_number} onChange={setU("id_card_number")} />
                </Field>
                <Field label="Ημερομηνία Έκδοσης" required error={errors.id_issue_date}>
                  <input data-testid="id-issue-date-input" type="date" className={inputCls("id_issue_date")} value={data.id_issue_date} onChange={set("id_issue_date")} />
                </Field>
                <Field label="Αστ. Τμήμα Έκδοσης" required error={errors.id_issue_dept} className="md:col-span-2">
                  <input data-testid="id-issue-dept-input" className={inputCls("id_issue_dept")} value={data.id_issue_dept} onChange={setU("id_issue_dept")} placeholder="π.χ. Α.Τ. ΣΥΝΤΑΓΜΑΤΟΣ" />
                </Field>
              </>
            )}

            {/* Conditional: Foreign ID fields */}
            {data.nationality === "foreign" && (
              <>
                <Field label="Αριθμός Διαβατηρίου" required error={errors.passport_number}>
                  <input data-testid="passport-number-input" className={inputCls("passport_number")} value={data.passport_number} onChange={setU("passport_number")} />
                </Field>
                <Field label="Ημερομηνία Έκδοσης" required error={errors.passport_issue_date}>
                  <input data-testid="passport-issue-date-input" type="date" className={inputCls("passport_issue_date")} value={data.passport_issue_date} onChange={set("passport_issue_date")} />
                </Field>
                <Field label="Κράτος Έκδοσης" required error={errors.passport_country}>
                  <input data-testid="passport-country-input" className={inputCls("passport_country")} value={data.passport_country} onChange={setU("passport_country")} />
                </Field>
                <Field label="Αριθμός Άδειας Αλλοδαπού" required error={errors.foreign_residence_permit}>
                  <input data-testid="foreign-permit-input" className={inputCls("foreign_residence_permit")} value={data.foreign_residence_permit} onChange={setU("foreign_residence_permit")} />
                </Field>
                <Field label="Ημερομηνία Λήξης Άδειας" required error={errors.foreign_residence_expiry} className="md:col-span-2">
                  <input data-testid="foreign-expiry-input" type="date" className={inputCls("foreign_residence_expiry")} value={data.foreign_residence_expiry} onChange={set("foreign_residence_expiry")} />
                </Field>
              </>
            )}
            <Field label="Οικογενειακή Κατάσταση">
              <select data-testid="marital-select" className="hf-input hf-select" value={data.marital_status} onChange={set("marital_status")}>
                <option value="">Επιλέξτε...</option>
                <option value="single">Άγαμος/η</option>
                <option value="married">Έγγαμος/η</option>
                <option value="divorced">Διαζευγμένος/η</option>
                <option value="widowed">Χήρος/α</option>
              </select>
            </Field>
            <Field label="Αριθμός Τέκνων">
              <input data-testid="children-count-input" type="number" min="0" className="hf-input" value={data.children_count} onChange={set("children_count")} />
            </Field>
            <Field label="Τέκνα Κάτω των 18 Ετών" className="md:col-span-2">
              <input data-testid="children-under-18-input" type="number" min="0" className="hf-input" value={data.children_under_18} onChange={set("children_under_18")} />
            </Field>
          </div>
        </SectionCard>

        {/* Section 3: Contact */}
        <SectionCard
          testId="section-contact"
          icon={<Phone size={22} strokeWidth={2.2} />}
          title="Στοιχεία Επικοινωνίας"
          subtitle="Τηλέφωνα και διεύθυνση"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Σταθερό Τηλέφωνο">
              <input data-testid="phone-landline-input" className="hf-input" value={data.phone_landline} onChange={set("phone_landline")} />
            </Field>
            <Field label="Κινητό Τηλέφωνο" required error={errors.phone_mobile}>
              <input data-testid="phone-mobile-input" className={inputCls("phone_mobile")} value={data.phone_mobile} onChange={set("phone_mobile")} />
            </Field>
            <Field label="Email">
              <input data-testid="email-input" type="email" className="hf-input" value={data.email} onChange={set("email")} />
            </Field>
            <Field label="Επιβεβαίωση Email" error={errors.email_confirm}>
              <input data-testid="email-confirm-input" type="email" className={inputCls("email_confirm")} value={data.email_confirm} onChange={set("email_confirm")} />
            </Field>
            <Field label="Κατοικία (Οδός - Αριθμός - ΤΚ - Πόλη)" required error={errors.address} className="md:col-span-2">
              <input data-testid="address-input" className={inputCls("address")} value={data.address} onChange={setU("address")} placeholder="π.χ. ΠΑΝΕΠΙΣΤΗΜΙΟΥ 12 - 10671 - ΑΘΗΝΑ" />
            </Field>
          </div>
        </SectionCard>

        {/* Section 4: Insurance */}
        <SectionCard
          testId="section-insurance"
          icon={<Shield size={22} strokeWidth={2.2} />}
          title="Ασφαλιστικά Στοιχεία"
          subtitle="ΑΦΜ, ΑΜΚΑ και ασφαλιστικά"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="ΑΦΜ" required error={errors.personal_afm}>
              <input data-testid="personal-afm-input" inputMode="numeric" maxLength={9} className={inputCls("personal_afm")} value={data.personal_afm} onChange={set("personal_afm")} />
            </Field>
            <Field label="ΑΜΚΑ" required error={errors.amka}>
              <input data-testid="amka-input" inputMode="numeric" maxLength={11} className={inputCls("amka")} value={data.amka} onChange={set("amka")} />
            </Field>
            <Field label="ΑΜ ΙΚΑ">
              <input data-testid="am-ika-input" className="hf-input" value={data.am_ika} onChange={set("am_ika")} />
            </Field>
            <Field label="ΠΑ">
              <input data-testid="pa-input" className="hf-input" value={data.pa} onChange={set("pa")} />
            </Field>
            <Field label="Έτος Πρώτης Ασφάλισης" className="md:col-span-2">
              <input data-testid="first-insurance-year-input" inputMode="numeric" maxLength={4} className="hf-input" value={data.first_insurance_year} onChange={set("first_insurance_year")} />
            </Field>
          </div>
        </SectionCard>

        {/* Section 5: Experience & Resume */}
        <SectionCard
          testId="section-experience"
          icon={<Briefcase size={22} strokeWidth={2.2} />}
          title="Προϋπηρεσία & Βιογραφικό"
          subtitle="Εμπειρία και ανέβασμα βιογραφικού"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Έτη Γενικής Προϋπηρεσίας" required error={errors.general_experience_years}>
              <input data-testid="general-exp-input" type="number" min="0" step="0.5" className={inputCls("general_experience_years")} value={data.general_experience_years} onChange={set("general_experience_years")} />
            </Field>
            <Field label="Έτη Προϋπηρεσίας στην Ειδικότητα" required error={errors.specialty_experience_years}>
              <input data-testid="specialty-exp-input" type="number" min="0" step="0.5" className={inputCls("specialty_experience_years")} value={data.specialty_experience_years} onChange={set("specialty_experience_years")} />
            </Field>
          </div>

          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 font-medium">
            <span className="inline-block mr-1">📋</span>
            Για την απόδειξη της Προϋπηρεσίας θα πρέπει να σταλεί στο λογιστήριο η <b>«Βεβαίωση Προϋπηρεσίας ΕΦΚΑ»</b> και <b>«Ατομικός Λογαριασμός Ασφάλισης ΕΦΚΑ»</b>.
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Resume upload — multiple files */}
            <div>
              <label className="hf-label">Βιογραφικό & Πιστοποιητικά (Πολλαπλά)</label>
              <input
                ref={resumeInput}
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);
                  setResumes((prev) => [...prev, ...newFiles]);
                  e.target.value = "";
                }}
                data-testid="resume-file-input"
              />
              <button
                type="button"
                data-testid="resume-upload-btn"
                onClick={() => resumeInput.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 h-32 border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-blue-50 hover:border-blue-400 rounded-xl transition-all group"
              >
                <Upload className="text-slate-400 group-hover:text-blue-600 transition" size={28} />
                <span className="text-sm font-bold text-slate-700">
                  {resumes.length > 0 ? "Προσθήκη κι άλλου αρχείου" : "Επιλογή αρχείων"}
                </span>
                <span className="text-xs text-slate-500">PDF, DOC, DOCX, Εικόνες — μπορείτε πολλά</span>
              </button>
            </div>

            {/* Camera capture — adds to same list */}
            <div>
              <label className="hf-label">Φωτογραφία από Κάμερα</label>
              <input
                ref={photoInput}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setResumes((prev) => [...prev, f]);
                  e.target.value = "";
                }}
                data-testid="photo-camera-input"
              />
              <button
                type="button"
                data-testid="photo-camera-btn"
                onClick={() => photoInput.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-2 h-32 border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-blue-50 hover:border-blue-400 rounded-xl transition-all group"
              >
                <Camera className="text-slate-400 group-hover:text-blue-600 transition" size={28} />
                <span className="text-sm font-bold text-slate-700">Λήψη φωτογραφίας</span>
                <span className="text-xs text-slate-500">Από κάμερα κινητού</span>
              </button>
            </div>
          </div>

          {/* List of selected files */}
          {resumes.length > 0 && (
            <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="resumes-list">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center justify-between">
                <span>📎 Επιλεγμένα αρχεία ({resumes.length})</span>
                <button
                  type="button"
                  onClick={() => setResumes([])}
                  className="text-xs text-red-600 hover:text-red-800 font-bold"
                  data-testid="clear-all-files-btn"
                >
                  Καθαρισμός όλων
                </button>
              </div>
              <ul className="space-y-2">
                {resumes.map((f, idx) => (
                  <li
                    key={`${f.name}-${idx}`}
                    className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2"
                    data-testid={`resume-file-item-${idx}`}
                  >
                    <FileText className="text-blue-700 shrink-0" size={18} />
                    <span className="text-sm font-medium text-slate-800 truncate flex-1" title={f.name}>
                      {f.name}
                    </span>
                    <span className="text-xs text-slate-400 font-mono shrink-0">
                      {(f.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => setResumes((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-slate-400 hover:text-red-600 transition shrink-0"
                      title="Αφαίρεση"
                      data-testid={`remove-resume-${idx}`}
                    >
                      <X size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </SectionCard>

        {/* Section 6: Hiring */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm font-medium text-amber-900">
          ⚠️ Η συμπλήρωση των παρακάτω πεδίων γίνεται μόνο μετά από συμφωνία με τον εργοδότη.
        </div>
        <SectionCard
          testId="section-hiring"
          icon={<FileCheck size={22} strokeWidth={2.2} />}
          title="Στοιχεία Πρόσληψης"
          subtitle="Συμπληρώνεται κατόπιν συμφωνίας"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="Ημερομηνία Πρόσληψης" required error={errors.hire_date}>
              <input data-testid="hire-date-input" type="date" className={inputCls("hire_date")} value={data.hire_date} onChange={set("hire_date")} />
            </Field>
            <Field label="Ειδικότητα" required error={errors.specialty}>
              <input data-testid="specialty-input" className={inputCls("specialty")} value={data.specialty} onChange={setU("specialty")} />
            </Field>
            <Field label="Μισθός" required error={errors.salary_type}>
              <select data-testid="salary-select" className={`${inputCls("salary_type")} hf-select`} value={data.salary_type} onChange={set("salary_type")}>
                <option value="">Επιλέξτε τύπο μισθού</option>
                <option value="basic">Βασικός</option>
                <option value="agreed">Συμφωνηθείς</option>
              </select>
            </Field>
            {/* Conditional: salary period + amount when agreed */}
            {data.salary_type === "agreed" ? (
              <Field label="Είδος Αμοιβής" required error={errors.salary_period}>
                <select data-testid="salary-period-select" className={`${inputCls("salary_period")} hf-select`} value={data.salary_period} onChange={set("salary_period")}>
                  <option value="">Επιλέξτε...</option>
                  <option value="monthly">Μηνιαία</option>
                  <option value="daily">Ημερήσια</option>
                </select>
              </Field>
            ) : <div className="hidden md:block" />}
            {data.salary_type === "agreed" && (
              <Field
                label={`Ποσό ${data.salary_period === "daily" ? "Ημερήσιας" : "Μηνιαίας"} Αμοιβής (€)`}
                required
                error={errors.salary_amount}
                className="md:col-span-2"
              >
                <input
                  data-testid="salary-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputCls("salary_amount")}
                  value={data.salary_amount}
                  onChange={set("salary_amount")}
                  placeholder={data.salary_period === "daily" ? "π.χ. 45" : "π.χ. 1200"}
                />
                {/* Warning: monthly < 830€ basic 2026 */}
                {data.salary_period === "monthly" && data.salary_amount && Number(data.salary_amount) > 0 && Number(data.salary_amount) < MIN_MONTHLY_2026 && (
                  <p className="text-xs text-amber-700 font-semibold mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="salary-warning">
                    ⚠ Το ποσό είναι κάτω από τον βασικό μηνιαίο μισθό 2026 (€{MIN_MONTHLY_2026}). Παρακαλούμε επιβεβαιώστε.
                  </p>
                )}
              </Field>
            )}

            <Field label="Σύμβαση" required error={errors.contract_type}>
              <select data-testid="contract-select" className={`${inputCls("contract_type")} hf-select`} value={data.contract_type} onChange={set("contract_type")}>
                <option value="">Επιλέξτε τύπο σύμβασης</option>
                <option value="indefinite">Αορίστου Χρόνου</option>
                <option value="definite">Ορισμένου Χρόνου</option>
              </select>
            </Field>
            {/* Conditional: contract end date when definite */}
            {data.contract_type === "definite" ? (
              <Field label="Ημερομηνία Λήξης Σύμβασης" required error={errors.contract_end_date}>
                <input
                  data-testid="contract-end-date-input"
                  type="date"
                  className={inputCls("contract_end_date")}
                  value={data.contract_end_date}
                  onChange={set("contract_end_date")}
                />
              </Field>
            ) : <div className="hidden md:block" />}

            <Field label="Πλήρης ή Μερική Απασχόληση" required error={errors.employment_type}>
              <select data-testid="employment-select" className={`${inputCls("employment_type")} hf-select`} value={data.employment_type} onChange={set("employment_type")}>
                <option value="">Επιλέξτε τύπο απασχόλησης</option>
                <option value="full_time">Πλήρης Απασχόληση</option>
                <option value="part_time">Μερική Απασχόληση</option>
              </select>
            </Field>
            <Field label="Πρόγραμμα" required error={errors.schedule_type}>
              <select data-testid="schedule-select" className={`${inputCls("schedule_type")} hf-select`} value={data.schedule_type} onChange={set("schedule_type")}>
                <option value="">Επιλέξτε πρόγραμμα</option>
                <option value="fixed">Σταθερό Πρόγραμμα</option>
                <option value="shifts">Βάρδιες</option>
              </select>
            </Field>
          </div>

          {/* Conditional: schedule details when fixed */}
          {data.schedule_type === "fixed" && (
            <div className="mt-6 p-5 bg-blue-50/40 border border-blue-100 rounded-xl hf-fade-up">
              <h4 className="text-sm font-extrabold uppercase tracking-wider text-blue-900 mb-4">
                Σταθερό Πρόγραμμα
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <Field label="Ωράριο (π.χ. 07:00-19:00)" required error={errors.schedule_hours}>
                  <input
                    data-testid="schedule-hours-input"
                    className={inputCls("schedule_hours")}
                    value={data.schedule_hours}
                    onChange={set("schedule_hours")}
                    onBlur={(e) => setData({ ...data, schedule_hours: formatHoursRange(e.target.value) })}
                    placeholder="07:00-19:00"
                  />
                </Field>
                <div>
                  <label className={`hf-label hf-required`}>Ημέρες Εβδομάδας</label>
                  <div className="flex flex-wrap gap-2" data-testid="schedule-days-group">
                    {DAYS_OF_WEEK.map((d) => {
                      const checked = (data.schedule_days || []).includes(d.code);
                      return (
                        <label
                          key={d.code}
                          className={`cursor-pointer select-none px-3 py-2 rounded-lg border text-sm font-bold transition ${
                            checked
                              ? "bg-blue-700 text-white border-blue-700 shadow-sm"
                              : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            data-testid={`day-${d.short}`}
                            checked={checked}
                            onChange={() => {
                              const cur = Array.isArray(data.schedule_days) ? [...data.schedule_days] : [];
                              if (checked) {
                                setData({ ...data, schedule_days: cur.filter((x) => x !== d.code) });
                              } else {
                                setData({ ...data, schedule_days: [...cur, d.code] });
                              }
                            }}
                          />
                          {d.short}
                        </label>
                      );
                    })}
                  </div>
                  {errors.schedule_days && (
                    <p className="text-xs text-red-600 font-semibold mt-2" data-testid="schedule-days-error">
                      {errors.schedule_days}
                    </p>
                  )}
                </div>
              </div>

              {/* Hours × days summary */}
              {(() => {
                const hrs = parseHoursRange(data.schedule_hours);
                const days = (data.schedule_days || []).length;
                if (hrs === null || days === 0) return null;
                const total = hrs * days;
                const over = total > 40;
                return (
                  <div
                    className={`mt-5 px-4 py-3 rounded-xl text-sm font-bold flex items-center justify-between ${
                      over
                        ? "bg-red-50 text-red-700 border border-red-200 hf-shake"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                    data-testid="hours-summary"
                  >
                    <span>
                      Σύνολο: <span className="font-mono">{hrs.toFixed(1)}h × {days} ημέρες = {total.toFixed(1)}h / εβδομάδα</span>
                    </span>
                    {over && <span data-testid="hours-overload-msg">⚠ Υπέρβαση Ωραρίου</span>}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Conditional: shifts schedule (Βάρδιες) — per-day start/end */}
          {data.schedule_type === "shifts" && (
            <div className="mt-6 p-5 bg-blue-50/40 border border-blue-100 rounded-xl hf-fade-up">
              <h4 className="text-sm font-extrabold uppercase tracking-wider text-blue-900 mb-1">
                Πρόγραμμα Βαρδιών
              </h4>
              <p className="text-xs text-slate-500 font-medium mb-5">
                Ώρες εργασίας πρώτης εβδομάδας — αφήστε κενό σε όποια ημέρα δεν εργάζεται.
              </p>
              <div className="space-y-2" data-testid="shifts-grid">
                {DAYS_OF_WEEK.map((d) => {
                  const day = data.shift_schedule?.[d.code] || {};
                  const updateDay = (k, v) => {
                    const next = { ...(data.shift_schedule || {}) };
                    next[d.code] = { ...(next[d.code] || {}), [k]: v };
                    if (!next[d.code].start && !next[d.code].end) delete next[d.code];
                    setData({ ...data, shift_schedule: next });
                  };
                  return (
                    <div key={d.code} className="grid grid-cols-12 gap-2 items-center bg-white border border-slate-200 rounded-xl px-3 py-2">
                      <div className="col-span-12 md:col-span-3 text-sm font-bold text-slate-800">{d.code}</div>
                      <div className="col-span-6 md:col-span-4">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Έναρξη</label>
                        <input
                          type="time"
                          data-testid={`shift-${d.short}-start`}
                          className="w-full h-9 px-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600"
                          value={day.start || ""}
                          onChange={(e) => updateDay("start", e.target.value)}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-4">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Λήξη</label>
                        <input
                          type="time"
                          data-testid={`shift-${d.short}-end`}
                          className="w-full h-9 px-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600"
                          value={day.end || ""}
                          onChange={(e) => updateDay("end", e.target.value)}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-1 text-right">
                        {(day.start || day.end) && (
                          <button
                            type="button"
                            data-testid={`shift-${d.short}-clear`}
                            onClick={() => {
                              const next = { ...(data.shift_schedule || {}) };
                              delete next[d.code];
                              setData({ ...data, shift_schedule: next });
                            }}
                            className="text-xs text-slate-400 hover:text-red-600 font-bold"
                            title="Καθαρισμός"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {(() => {
                const total = calcShiftTotalHours(data.shift_schedule);
                if (total <= 0) return null;
                const over = total > 40;
                return (
                  <div
                    className={`mt-5 px-4 py-3 rounded-xl text-sm font-bold flex items-center justify-between ${
                      over
                        ? "bg-red-50 text-red-700 border border-red-200 hf-shake"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                    data-testid="shifts-summary"
                  >
                    <span>Σύνολο: <span className="font-mono">{total.toFixed(1)}h / εβδομάδα</span></span>
                    {over && <span data-testid="shifts-overload-msg">⚠ Υπέρβαση Ωραρίου</span>}
                  </div>
                );
              })()}
            </div>
          )}
        </SectionCard>

        {/* Notes — free-form */}
        <SectionCard
          testId="section-notes"
          icon={<FileText size={22} strokeWidth={2.2} />}
          title="Σημειώσεις"
          subtitle="Προαιρετικά — γράψτε ό,τι θέλετε ελεύθερα"
        >
          <textarea
            data-testid="notes-textarea"
            className="hf-input hf-textarea w-full"
            rows={5}
            value={data.notes}
            onChange={set("notes")}
            placeholder="π.χ. ειδικές παρατηρήσεις, διαθεσιμότητα, αιτήματα..."
            maxLength={2000}
          />
          <p className="text-xs text-slate-400 font-medium mt-2 text-right">
            {(data.notes || "").length} / 2000
          </p>
        </SectionCard>

        {/* Submit */}
        {globalError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-sm text-red-700 font-semibold flex items-start gap-3" data-testid="global-error">
            <AlertCircle size={20} className="shrink-0 mt-0.5" />
            <span>{globalError}</span>
          </div>
        )}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-sm text-red-700 font-semibold" data-testid="form-errors-summary">
            Παρακαλώ συμπληρώστε τα υποχρεωτικά πεδία ({Object.keys(errors).length}).
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-3 justify-end">
          <button
            type="button"
            onClick={() => { if (window.confirm("Διαγραφή όλων των πεδίων;")) { setData(initialState); setResumes([]); localStorage.removeItem("hireflow_draft"); } }}
            className="h-14 px-6 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-xl transition-all"
            data-testid="reset-form-btn"
          >
            Καθαρισμός
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="h-14 px-10 bg-blue-700 hover:bg-blue-800 text-white font-bold text-lg rounded-xl shadow-[0_8px_20px_-8px_rgba(29,78,216,0.5)] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="submit-form-btn"
          >
            {submitting ? <><Loader2 className="animate-spin" size={20} /> Δημιουργία προεπισκόπησης…</> : <>Προεπισκόπηση & Υποβολή <FileCheck size={20} /></>}
          </button>
        </div>

        <footer className="text-center text-xs text-slate-400 mt-12">
          HireFlow · <a href="/admin" className="hover:text-slate-600 underline">Διαχείριση</a>
        </footer>
      </form>
    </div>
  );
}
